import { log } from '@/logger/logger.ts';
import { nextLogSequenceId } from '@/logger/page-load.ts';
import {
  EXPECTED_TERMINAL_DETAILS,
  WAFClearanceResolveState,
  WAFTelemetryOutcomeExtra,
  WAFTelemetryPhase,
  type WAFTelemetryEvent,
  type WAFTelemetryOutcome,
} from './types';

const logger = log.getLogger('WAFChallenge');

// Nothing logs at `debug`, which the Datadog middleware drops, so every WAF event reaches Datadog and the
// log-derived rate metrics count each occurrence — except three latched once per session: `opaque-network`
// (in axiosConfig), the `missing-sitekey` misconfig (across both transports in recoverWAFChallenge), and
// `cross-tab-unavailable` (in crossTab), so those metrics count sessions rather than occurrences. `widget-error`
// is recorded once per error code per clearance (in CloudflareTurnstileClearanceProvider).
const INFO_OUTCOMES = new Set<WAFTelemetryOutcome>([
  WAFTelemetryOutcomeExtra.Challenge,
  WAFClearanceResolveState.Solved,
  WAFTelemetryOutcomeExtra.Recovered,
  WAFTelemetryOutcomeExtra.WidgetNeedsInteraction,
  WAFTelemetryOutcomeExtra.WidgetAfterInteractive,
  WAFTelemetryOutcomeExtra.FailureModalShown,
  WAFTelemetryOutcomeExtra.FailureModalReload,
  WAFTelemetryOutcomeExtra.SlowNoticeReload,
  WAFTelemetryOutcomeExtra.ScriptLoaded,
  WAFTelemetryOutcomeExtra.InteractiveHelpShown,
  WAFTelemetryOutcomeExtra.InteractiveHelpReload,
  WAFTelemetryOutcomeExtra.GateClosed,
  WAFTelemetryOutcomeExtra.CrossTabUnavailable,
]);

// Unrecoverable even though it is a detection rather than a terminal-phase event, so it logs at error too.
const ERROR_OUTCOMES = new Set<WAFTelemetryOutcome>([WAFTelemetryOutcomeExtra.OpaqueNetwork]);

function levelForEvent(event: WAFTelemetryEvent): 'info' | 'warn' | 'error' {
  if (INFO_OUTCOMES.has(event.outcome)) {
    return 'info';
  }
  if (
    event.phase === WAFTelemetryPhase.Terminal &&
    event.detail !== undefined &&
    EXPECTED_TERMINAL_DETAILS.has(event.detail)
  ) {
    return 'warn';
  }
  if (ERROR_OUTCOMES.has(event.outcome) || event.phase === WAFTelemetryPhase.Terminal) {
    return 'error';
  }
  return 'warn';
}

// Guaranteed non-throwing: callers invoke this unguarded, so only the pure sequence-id increment runs
// outside the try. Keep every other statement (context build, level derivation, log dispatch) inside it.
export function recordWAFTelemetry(event: WAFTelemetryEvent): number {
  const sequenceId = nextLogSequenceId();
  try {
    const context = {
      sequenceId,
      phase: event.phase,
      outcome: event.outcome,
      ...(event.wafProvider !== undefined ? { wafProvider: event.wafProvider } : {}),
      ...(event.transport !== undefined ? { transport: event.transport } : {}),
      ...(event.durationMs !== undefined ? { durationMs: event.durationMs } : {}),
      ...(event.pageVisibilityState !== undefined ? { pageVisibilityState: event.pageVisibilityState } : {}),
      ...(event.replayCount !== undefined ? { replayCount: event.replayCount } : {}),
      ...(event.clearanceRole !== undefined ? { clearanceRole: event.clearanceRole } : {}),
      ...(event.wasEverInteractive !== undefined ? { wasEverInteractive: event.wasEverInteractive } : {}),
      ...(event.wasEverSlow !== undefined ? { wasEverSlow: event.wasEverSlow } : {}),
      ...(event.detail !== undefined ? { detail: event.detail } : {}),
      ...(event.providerErrorCode !== undefined ? { providerErrorCode: event.providerErrorCode } : {}),
      ...(event.terminalOutcome !== undefined ? { terminalOutcome: event.terminalOutcome } : {}),
      ...(event.isLoginFlowRequest !== undefined ? { isLoginFlowRequest: event.isLoginFlowRequest } : {}),
      ...(event.clearanceStage !== undefined ? { clearanceStage: event.clearanceStage } : {}),
      ...(event.clearanceCookieIssued !== undefined ? { clearanceCookieIssued: event.clearanceCookieIssued } : {}),
      ...(event.scriptLoadDurationMs !== undefined ? { scriptLoadDurationMs: event.scriptLoadDurationMs } : {}),
      ...(event.wasScriptAlreadyLoaded !== undefined ? { wasScriptAlreadyLoaded: event.wasScriptAlreadyLoaded } : {}),
    };
    logger[levelForEvent(event)]('WAF challenge telemetry', context);
  } catch {
    // A telemetry failure must not break the WAF flow; the sequenceId is still returned.
  }
  return sequenceId;
}
